import { useState, useEffect, useCallback, useRef } from "react";
import DynamicTable from "../../ui/DynamicTable";
import config from "../../../config/config";
import { refreshDashboardData } from "../../../observers/dataObserver";
import { refreshUserTableData } from "../../../observers/dataObserver";
import { BsThreeDotsVertical } from "react-icons/bs";
import Pagination from "../../ui/Pagination";
import { userSession } from "../../../observers/userObserver";
import { CODInput } from "../../ui/CODinput";

const AllUserTable = () => {
  const [dropdownOpen, setDropdownOpen] = useState({});
  const dropdownRef = useRef({});
  const [aiSearchChecked, setAiSearchChecked] = useState({});
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    username: "",
    company_name: ""
  });
  const [appliedFilters, setAppliedFilters] = useState({
    username: "",
    company_name: ""
  })

  const token = userSession.getToken();
  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  });

  const fetchCODData = useCallback(async () => {
    try {
      let url = `${config["backend-url"]}/all_users?page=${pagination.current_page}&limit=${pagination.limit}`;

      if (appliedFilters.username) {
        url += `&username=${encodeURIComponent(appliedFilters.username)}`;
      }
      if (appliedFilters.company_name) {
        url += `&company_name=${encodeURIComponent(appliedFilters.company_name)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization:`Bearer ${token}` },
      });

      const data = await response.json();
      if (response.ok) {
        const users = data.data.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          user_status: user.user_status,
          user_type: user.user_type,
          company_name: user.company_name || "N/A",
          MLNOFlag: user.MLNOFlag,
          two_fa_enabled: user.two_fa_enabled,
          totp_enabled: user.totp_enabled,
          ai_search_enabled: user.ai_search_enabled,
        }));
        setAiSearchChecked(data.data.reduce((acc, user) => ({ ...acc, [user.id]: user.ai_search_enabled }), {}));
        setPagination(data.pagination);
        setRows(users);
      } else {
        console.error("❌ Failed to load:", data.error);
      }
    } catch (error) {
      console.error("❌ Network or server error:", error);
    }
  }, [pagination.current_page, pagination.limit, appliedFilters, token]);

  const toggleAISearch = useCallback(async (userId, currentStatus) => {
    const newStatus = !currentStatus;
    setAiSearchChecked((prev) => ({ ...prev, [userId]: newStatus }));
    try {
      const response = await fetch(`${config["backend-url"]}/toggle_ai_search`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, enabled: newStatus }),
      });
      const data = await response.json();
      if (!data.success) {
        setAiSearchChecked((prev) => ({ ...prev, [userId]: currentStatus }));
        alert(data.error || "Failed to update AI Search");
      }
    } catch (error) {
      setAiSearchChecked((prev) => ({ ...prev, [userId]: currentStatus }));
      console.error("❌ Network error:", error);
    }
  }, [token]);

  const toggle2FA = useCallback(async (userId, enable) => {
    if (!window.confirm(`${enable ? "Enable" : "Disable"} 2FA for this user?`)) return;
    try {
      const response = await fetch(`${config["backend-url"]}/toggle_2fa`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, enabled: enable }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchCODData();
      } else {
        alert(`Failed to ${enable ? "enable" : "disable"} 2FA: ` + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  }, [fetchCODData]);

  const resetUserTOTP = useCallback(async (userId) => {
    if (!window.confirm("Reset this user's authenticator app? They will need to set it up again on their next login.")) return;
    try {
      const response = await fetch(`${config["backend-url"]}/totp/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchCODData();
      } else {
        alert("Failed to reset TOTP: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  }, [fetchCODData]);

  const changeUserStatus = useCallback(async (userId, newStatus) => {
    try {
      const response = await fetch(`${config["backend-url"]}/Change_User_Status`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, User_Status: newStatus }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("✅ Status updated:", data.message);
        refreshDashboardData.push("refresh");
        setPagination((prevPagination) => ({ ...prevPagination, current_page: 1 }));
        fetchCODData(); // Refresh data after status update
      } else {
        console.error("❌ Error updating status:", data.error);
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  },[fetchCODData]);

  const handlePageChange = (pageNumber) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: pageNumber
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      username: "",
      company_name: ""
    });
    setAppliedFilters({
      username: "",
      company_name: ""
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  useEffect(() => {
    fetchCODData();

    refreshUserTableData.subscribe((value) => {
      if (value === "refresh") {
        fetchCODData();
      }
    });

  }, [fetchCODData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = Object.keys(dropdownRef.current).every(key => {
        const element = dropdownRef.current[key];
        return element && !element.contains(event.target);
      });

      if (isOutside) {
        setDropdownOpen({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderStatus = useCallback((value) => (
    <span className={`status-tile status-${value}`}>{value}</span>
  ),[]);

  const toggleDropdown = useCallback((id) => {
    setDropdownOpen((prev) => {
        const isOpen = prev[id];
        return isOpen
        ? {} // Close all if current is open
        : { [id]: true }; // Open only the current one
    });
},[]);

  const renderActionDropdown = useCallback((_, row) => {
    return (
        <div
            className="dropdown-container"
            key={row.id}
            ref={(el) => {
                if (el) {
                    dropdownRef.current[row.id] = el;
                } else {
                    delete dropdownRef.current[row.id];
                }
            }}
        >
        <button
            onClick={() => toggleDropdown(row.id)}
            className="dropdown-btn"
        >
            <BsThreeDotsVertical size={20} />
        </button>
        {dropdownOpen[row.id] && (
            <div className="custom-dropdown">
            {row.user_status?.toLowerCase() === "pending" && (
                <>
                <div
                    className="dropdownItem"
                    onClick={() => changeUserStatus(row.id, "Active")}
                >
                    Activate
                </div>
                <div
                    className="dropdownItem"
                    onClick={() => changeUserStatus(row.id, "Inactive")}
                >
                    Deactivate
                </div>
                </>
            )}
            {row.user_status?.toLowerCase() === "active" && (
                <div
                className="dropdownItem"
                onClick={() => changeUserStatus(row.id, "Inactive")}
                >
                Deactivate
                </div>
            )}
            {row.user_status?.toLowerCase() === "inactive" && (
                <div
                className="dropdownItem"
                onClick={() => changeUserStatus(row.id, "Active")}
                >
                Activate
                </div>
            )}
            {!row.two_fa_enabled && (
                <div
                className="dropdownItem"
                onClick={() => toggle2FA(row.id, true)}
                >
                Enable 2FA
                </div>
            )}
            {row.two_fa_enabled && (
                <div
                className="dropdownItem"
                onClick={() => toggle2FA(row.id, false)}
                >
                Disable 2FA
                </div>
            )}
            {row.totp_enabled && (
                <div
                className="dropdownItem"
                onClick={() => resetUserTOTP(row.id)}
                >
                Reset TOTP
                </div>
            )}
            </div>
        )}
        </div>
    );
    },[changeUserStatus, toggle2FA, resetUserTOTP, dropdownOpen, toggleDropdown]);

  const render2FAStatus = useCallback((_, row) => {
    if (!row.two_fa_enabled) return <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Off</span>;
    if (row.totp_enabled) return (
      <span style={{ background: "#002961", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontSize: "0.75rem", fontWeight: 600 }}>
        TOTP
      </span>
    );
    return (
      <span style={{ background: "#6c757d", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontSize: "0.75rem", fontWeight: 600 }}>
        Email OTP
      </span>
    );
  }, []);

  const renderAISearchCheckbox = useCallback((_, row) => {
    const isEnabled = aiSearchChecked[row.id] || false;
    return (
      <input
        type="checkbox"
        checked={isEnabled}
        onChange={() => toggleAISearch(row.id, isEnabled)}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
    );
  }, [aiSearchChecked, toggleAISearch]);

  const columns = [
    { key: "name", label: "User Name" },
    { key: "email", label: "Email" },
    { key: "company_name", label: "Company Name" },
    { key: "user_status", label: "Status", render: renderStatus },
    { key: "two_fa_enabled", label: "2FA", render: render2FAStatus },
    { key: "action", label: "Action", render: renderActionDropdown },
    { key: "ai_search_enabled", label: "AI Search", render: renderAISearchCheckbox },
  ];

  return (
    <>
      <div className="filter-section" style={{
        marginBottom: "20px",
       width: "100%",
        gap: "10px",
        alignItems: "flex-end"
      }}>
        <div style={{ flex: "1 1 auto", minWidth: "0" }}>
          <CODInput
            label="User Name"
            name="username"
            type="text"
            value={filters.username}
            placeholder="Enter username"
            onChange={(e) => handleFilterChange("username", e.target.value)}
          />
        </div>
        <div style={{ flex: "1 1 auto", minWidth: "0" }}>
          <CODInput
            label="Company Name"
            name="company_name"
            type="text"
            value={filters.company_name}
            placeholder="Enter company name"
            onChange={(e) => handleFilterChange("company_name", e.target.value)}
          />
        </div>
        <button
          onClick={handleApplyFilters}
          style={{
            padding: "8px 20px",
            backgroundColor: "#07b1b0",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            whiteSpace: "nowrap",
            flexShrink: "0"
          }}
        >
          Filter
        </button>
        <button
          onClick={handleResetFilters}
          style={{
            padding: "8px 20px",
            backgroundColor: "rgb(204 204 204)",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            whiteSpace: "nowrap",
            flexShrink: "0"
          }}
        >
          Reset
        </button>
      </div>
      <DynamicTable data={rows} columns={columns} />
      <Pagination totalPages={pagination.total_pages} page={pagination.current_page} handlePageChange={handlePageChange} />
    </>
  );
};

export default AllUserTable;
